/**
 * @module CaseActionPublic
 * @description Public token-based case action page (no login required).
 * Used for one-click resolution from stage-change notification emails.
 * Customer can accept a proposed solution or request further help.
 *
 * @route /case-action/:token
 * @since v2.35.0
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

// ─── Star Rating ─────────────────────────────────────────────────

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="text-3xl transition-all duration-150 focus:outline-none"
          style={{
            color: star <= (hover || value) ? '#F59E0B' : '#D1D5DB',
            transform: star <= (hover || value) ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          &#9733;
        </button>
      ))}
      {value > 0 && (
        <span className="text-sm text-[var(--text-tertiary,#6B7280)] self-center ml-2">{value}/5</span>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export default function CaseActionPublic() {
  const { token } = useParams();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Form state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitAction, setSubmitAction] = useState(null);

  // Load token data
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/public/case-action/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError(t('caseAction.loadError')))
      .finally(() => setLoading(false));
  }, [token, t]);

  const handleAction = async (action) => {
    setSubmitting(true);
    setSubmitAction(action);
    try {
      const body = { action };
      if (action === 'accept_solution' && rating > 0) body.feedback_rating = rating;
      if (comment.trim()) body.feedback_text = comment.trim();

      const res = await fetch(`${API}/api/public/case-action/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.error || t('caseAction.submitError'));
        return;
      }
      setSubmitted(true);
    } catch {
      setError(t('caseAction.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Shell ──
  const Shell = ({ children }) => (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center shadow-sm">
            <span className="text-white font-extrabold text-sm">W</span>
          </div>
          <span className="text-xl font-extrabold text-[var(--text-primary,#1A1A2E)] tracking-tight">WeShip</span>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-6 sm:p-8">
          {children}
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-[var(--text-tertiary,#6B7280)] mt-6">
          WeShip Fulfillment &middot; app.weship.eu
        </p>
      </div>
    </div>
  );

  // ── Loading ──
  if (loading) {
    return (
      <Shell>
        <div className="flex flex-col items-center py-10">
          <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-4">{t('caseAction.loading')}</p>
        </div>
      </Shell>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <Shell>
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          </div>
          <h2 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] mb-2">{t('caseAction.errorTitle')}</h2>
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">{error}</p>
        </div>
      </Shell>
    );
  }

  // ── Submitted ──
  if (submitted) {
    const isAccept = submitAction === 'accept_solution';
    return (
      <Shell>
        <div className="text-center py-8">
          <div className={`w-14 h-14 rounded-full ${isAccept ? 'bg-green-100' : 'bg-primary-100'} flex items-center justify-center mx-auto mb-4`}>
            {isAccept ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            )}
          </div>
          <h2 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] mb-2">
            {isAccept ? t('caseAction.thankYou') : t('caseAction.helpRequested')}
          </h2>
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">
            {isAccept ? t('caseAction.caseClosed') : t('caseAction.caseReopened')}
          </p>
        </div>
      </Shell>
    );
  }

  // ── Already used ──
  if (data?.already_used) {
    return (
      <Shell>
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h2 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] mb-2">{t('caseAction.alreadyUsed')}</h2>
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">{t('caseAction.alreadyUsedDesc')}</p>
        </div>
      </Shell>
    );
  }

  // ── Action Form ──
  const caseData = data?.case || {};
  const isSolutionProposed = data?.action === 'accept_solution' || data?.action === 'request_help';

  return (
    <Shell>
      {/* Case info */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          {caseData.case_number && (
            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-primary-100 text-primary-600">
              #{caseData.case_number}
            </span>
          )}
          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-[var(--text-secondary,#4B5563)]">
            {caseData.stage || '—'}
          </span>
        </div>
        <h2 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] mt-2">{caseData.title || t('caseAction.case')}</h2>
        {caseData.description && (
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mt-1 line-clamp-3">{caseData.description}</p>
        )}
      </div>

      <hr className="border-[var(--border-table,#F3F4F6)] mb-6" />

      {/* Solution proposed → two-button UI */}
      {isSolutionProposed ? (
        <div className="space-y-6">
          <p className="text-sm text-[var(--text-secondary,#4B5563)]">{t('caseAction.solutionQuestion')}</p>

          {/* Rating (optional) */}
          <div>
            <label className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-2 block">
              {t('caseAction.ratingLabel')}
              <span className="text-xs font-normal text-[var(--text-tertiary,#6B7280)] ml-1">({t('common.optional') || 'optional'})</span>
            </label>
            <StarRating value={rating} onChange={setRating} />
          </div>

          {/* Comment (optional) */}
          <div>
            <label className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-2 block">
              {t('caseAction.commentLabel')}
              <span className="text-xs font-normal text-[var(--text-tertiary,#6B7280)] ml-1">({t('common.optional') || 'optional'})</span>
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              placeholder={t('caseAction.commentPlaceholder')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-default,#E5E7EB)] bg-gray-50 text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => handleAction('accept_solution')}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-white bg-green-500 hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              {submitting && submitAction === 'accept_solution' ? '...' : t('caseAction.acceptSolution')}
            </button>
            <button
              onClick={() => handleAction('request_help')}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-primary-600 bg-primary-50 hover:bg-primary-100 border border-primary-200 transition-colors disabled:opacity-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              {submitting && submitAction === 'request_help' ? '...' : t('caseAction.requestHelp')}
            </button>
          </div>
        </div>
      ) : (
        /* Generic action confirmation */
        <div className="text-center py-4">
          <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4">{t('caseAction.genericConfirm')}</p>
          <button
            onClick={() => handleAction(data?.action || 'confirm')}
            disabled={submitting}
            className="px-6 py-3 rounded-xl text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2b7bc4] transition-colors disabled:opacity-50"
          >
            {submitting ? '...' : t('caseAction.confirm')}
          </button>
        </div>
      )}
    </Shell>
  );
}
